import React from 'react';
import { Controller } from 'react-hook-form';
import { toFormattedDate } from 'utils/date';
import * as userHelpers from 'resources/user/user.helpers';
import * as dealHelpers from 'resources/deal/deal.helpers';
import { DEAL_TYPES } from 'resources/deal/deal.constants';
import Input from 'UI/Input';
import Switch from 'UI/Switch';
import Select from 'UI/Select';
import TextInput from 'UI/TextInput';
import DatePicker from 'UI/DatePicker';
import { Pane, Text } from 'evergreen-ui';
import styles from './styles.module.scss';

const formatDate = (date) => date && toFormattedDate(date);

export const ADVANCED_FIELDS_TYPES = {
  TEXT: 'text',
  DATE: 'date',
  SELECT: 'select',
  EMAIL: 'email',
  SWITCH: 'switch',
  PHONE: 'phone',
  TIME: 'time',
};

// fields to hide
export const HIDDEN_FIELDS = [
  'firstclientFirstName',
  'firstClientFullName',
  'secondClientFirstName',
  'secondClientFullName',
];

// list of common computed fields, which are using system data
// we have to store them to have an ability to fill data based on field variable
export const COMMON_COMPUTED_FIELDS = {
  AGENT_EMAIL: {
    VARIABLE: '{agentEmail}',
    NAME: 'agent_email',
  },
  AGENT_FIRST_NAME: {
    VARIABLE: '{agentFirstName}',
    NAME: 'agent_first_name',
  },
  AGENT_FULL_NAME: {
    VARIABLE: '{agentFullName}',
    NAME: 'agent_full_name',
  },
  AGENT_PHONE: {
    VARIABLE: '{agentPhone}',
    NAME: 'agent_phone',
  },
  AGENT_TITLE: {
    VARIABLE: '{agentTitle}',
    NAME: 'agent_title',
  },
  AGENT_LICENSE_NUMBER: {
    VARIABLE: '{agentLicenseNumber}',
    NAME: 'agent_license_number',
  },
  BROKERAGE_NAME: {
    VARIABLE: '{brokerageName}',
    NAME: 'brokerage_name',
  },
  CITY: {
    VARIABLE: '{city}',
    NAME: 'city',
  },
  CLOSING_DATE: {
    VARIABLE: '{closingDate}',
    NAME: 'closing_date',
  },
  FIRST_CLIENT_FIRST_NAME: {
    VARIABLE: '{firstClientFirstName}',
    NAME: 'first_client_first_name',
  },
  FIRST_CLIENT_FULL_NAME: {
    VARIABLE: '{firstClientFullName}',
    NAME: 'first_client_full_name',
  },
  OTHER_PARTY_NAME: {
    VARIABLE: '{otherPartyName}',
    NAME: 'other_party_name',
  },
  PROPERTY_ADDRESS: {
    VARIABLE: '{propertyAddress}',
    NAME: 'property_address',
  },
  SECOND_CLIENT_FIRST_NAME: {
    VARIABLE: '{secondClientFirstName}',
    NAME: 'second_client_first_name',
  },
  SECOND_CLIENT_FULL_NAME: {
    VARIABLE: '{secondClientFullName}',
    NAME: 'second_client_full_name',
  },
  STATE: {
    VARIABLE: '{state}',
    NAME: 'state',
  },
  STREET_ADDRESS: {
    VARIABLE: '{streetAddress}',
    NAME: 'street_address',
  },
  UNDER_CONTRACT_DATE: {
    VARIABLE: '{underContractDate}',
    NAME: 'underContractDate',
  },
  ZIP: {
    VARIABLE: '{zip}',
    NAME: 'zip',
  },
  SELLER_FULL_NAMES: {
    VARIABLE: '{sellerFullNames}',
    NAME: 'seller_full_names',
  },
  BUYER_FULL_NAMES: {
    VARIABLE: '{buyerFullNames}',
    NAME: 'buyer_full_names',
  },
};

// tooltips for fields
export const DISABLED_FIELDS_TOOLTIPS = {
  [COMMON_COMPUTED_FIELDS.CLOSING_DATE.NAME]: 'You can edit this field by scrolling to the top of the page under the "Transaction Details" section.',
  [COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.NAME]: 'You can edit this field by scrolling to the top of the page under the "Transaction Details" section.',
};

// list of fields to be filled while scaping with datafinity
export const DATAFINITI_ADVANCED_FIELDS = {
  COUNTY: {
    VARIABLE: '{county}',
    NAME: 'county',
  },
  MLS_NUMBER: {
    VARIABLE: '{mlsNumber}',
    NAME: 'mls_number',
  },
  CONSTRUCTION_TYPE: {
    VARIABLE: '{constructionType}',
    NAME: 'construction_type',
  },
  LEGAL_DESCRIPTION: {
    VARIABLE: '{legalDescription}',
    NAME: 'legal_description',
  },
  TAX_ID: {
    VARIABLE: '{taxId}',
    NAME: 'tax_id',
  },
  LISTING_PRICE: {
    VARIABLE: '{listingPrice}',
    NAME: 'listing_price',
  },
  CO_OPERATING_AGENT_NAME: {
    VARIABLE: '{coOperatingAgentName}',
    NAME: 'co_operating_agent_name',
  },
  CO_OPERATING_AGENT_BROKERAGE: {
    VARIABLE: '{coOperatingAgentBrokerage}',
    NAME: 'co_operating_agent_brokerage',
  },
  CO_OPERATING_AGENT_PHONE: {
    VARIABLE: '{coOperatingAgentPhone}',
    NAME: 'co_operating_agent_phone',
  },
};

// list of common fields for email templates
export const COMMON_EMAIL_TEMPLATES_FIELDS = {
  FIRST_CLIENT_FIRST_NAME: {
    VARIABLE: COMMON_COMPUTED_FIELDS.FIRST_CLIENT_FIRST_NAME.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.FIRST_CLIENT_FIRST_NAME.NAME,
  },
  FIRST_CLIENT_FULL_NAME: {
    VARIABLE: COMMON_COMPUTED_FIELDS.FIRST_CLIENT_FULL_NAME.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.FIRST_CLIENT_FULL_NAME.NAME,
  },
  SECOND_CLIENT_FIRST_NAME: {
    VARIABLE: COMMON_COMPUTED_FIELDS.SECOND_CLIENT_FIRST_NAME.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.SECOND_CLIENT_FIRST_NAME.NAME,
  },
  SECOND_CLIENT_FULL_NAME: {
    VARIABLE: COMMON_COMPUTED_FIELDS.SECOND_CLIENT_FULL_NAME.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.SECOND_CLIENT_FULL_NAME.NAME,
  },
  PROPERTY_ADDRESS: {
    VARIABLE: COMMON_COMPUTED_FIELDS.PROPERTY_ADDRESS.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.PROPERTY_ADDRESS.NAME,
  },
  CLOSING_DATE: {
    VARIABLE: COMMON_COMPUTED_FIELDS.CLOSING_DATE.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.CLOSING_DATE.NAME,
  },
  UNDER_CONTRACT_DATE: {
    VARIABLE: COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.NAME,
  },
  AGENT_FIRST_NAME: {
    VARIABLE: COMMON_COMPUTED_FIELDS.AGENT_FIRST_NAME.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.AGENT_FIRST_NAME.NAME,
  },
  AGENT_FULL_NAME: {
    VARIABLE: COMMON_COMPUTED_FIELDS.AGENT_FULL_NAME.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.AGENT_FULL_NAME.NAME,
  },
  AGENT_TITLE: {
    VARIABLE: COMMON_COMPUTED_FIELDS.AGENT_TITLE.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.AGENT_TITLE.NAME,
  },
  AGENT_LICENSE_NUMBER: {
    VARIABLE: COMMON_COMPUTED_FIELDS.AGENT_LICENSE_NUMBER.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.AGENT_LICENSE_NUMBER.NAME,
  },
  AGENT_PHONE: {
    VARIABLE: COMMON_COMPUTED_FIELDS.AGENT_PHONE.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.AGENT_PHONE.NAME,
  },
  AGENT_EMAIL: {
    VARIABLE: COMMON_COMPUTED_FIELDS.AGENT_EMAIL.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.AGENT_EMAIL.NAME,
  },
  BROKERAGE_NAME: {
    VARIABLE: COMMON_COMPUTED_FIELDS.BROKERAGE_NAME.VARIABLE,
    NAME: COMMON_COMPUTED_FIELDS.BROKERAGE_NAME.NAME,
  },
  PURCHASE_PRICE: {
    VARIABLE: '{purchasePrice}',
    NAME: 'purchase_price',
  },
};

const getDateField = (field) => ({
  key: field.name,
  componentType: Controller,
  componentProps: {
    name: field.name,
    render: ({ field: { onChange, value } }) => (
      <Pane display="flex" flexDirection="column" marginBottom={24}>
        {field.label && (
          <Pane marginBottom={8}>
            <Text color="var(--primary)" fontWeight={400} fontSize={14} lineHeight="24px" fontFamily="'Merriweather Sans', sans-serif">
              {field.label}
            </Text>
          </Pane>
        )}
        <Pane>
          <DatePicker
            value={value}
            onDateChange={onChange}
            disabled={field?.disabled}
          />
        </Pane>
      </Pane>
    ),
  },
});

const getInputField = (field) => ({
  key: field.name,
  componentType: Input,
  componentProps: {
    name: field.name,
    label: field.label,
    // id: field?.ID,
    disabled: field?.disabled,
  },
});

const getSwitchField = (field, props = {}) => ({
  key: field.name,
  componentType: Controller,
  componentProps: {
    name: field.name,
    render: ({ field: { onChange, value } }) => {
      // set up default value if it's not set
      if (value === undefined) {
        value = false;
        onChange(value);
      }

      return (
        <div className={styles.field}>
          {field.label && (
            <div className={styles.label}>{field.label}</div>
          )}
          <Switch checked={value} onChange={onChange} {...props} />
        </div>
      );
    },
  },
});

const getSelectField = (field) => ({
  key: field.name,
  componentType: Controller,
  componentProps: {
    name: field.name,
    render: ({ field: { onChange, value } }) => {
      let options = [];
      if (field.options) {
        options = field.options.map((option) => {
          // TODO: remove 'object' check after merging Default and Custom advanced fields
          // and store the options only as an array of strings
          if (typeof option === 'object') {
            return {
              label: option.label,
              value: option.value,
            };
          }

          return {
            label: option,
            value: option,
          };
        });

        // set up default value if it's not set
        if (value === undefined) {
          value = options[0]?.value;
          onChange(value);
        }
      }

      return (
        <Select
          label={field.label}
          value={value}
          onChange={onChange}
          options={options || []}
          className={styles.select}
        />
      );
    },
  },
});

const getTimeField = (field) => ({
  componentType: Controller,
  componentProps: {
    key: field.name,
    name: field.name,
    render: ({ field: { onChange, value } }) => (
      <TextInput
        label={field.label}
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={styles.field}
      />
    ),
  },
});

const getInputRendererByFieldType = {
  [ADVANCED_FIELDS_TYPES.TEXT]: getInputField,
  [ADVANCED_FIELDS_TYPES.SWITCH]: getSwitchField,
  [ADVANCED_FIELDS_TYPES.DATE]: getDateField,
  [ADVANCED_FIELDS_TYPES.SELECT]: getSelectField,
  [ADVANCED_FIELDS_TYPES.TIME]: getTimeField,
};

export const COMMON_COMPUTED_FIELD_FILLER_BY_VARIABLE = {
  [COMMON_COMPUTED_FIELDS.OTHER_PARTY_NAME.VARIABLE]: ({ deal }) => {
    const buyerOrSeller = getBuyerOrSeller(deal);
    return deal?.advanced_fields?.[buyerOrSeller.NAME] || '';
  },

  [COMMON_COMPUTED_FIELDS.FIRST_CLIENT_FIRST_NAME.VARIABLE]: ({ clients }) => clients?.[0]?.first_name || '',

  [COMMON_COMPUTED_FIELDS.FIRST_CLIENT_FULL_NAME.VARIABLE]: ({ clients }) => clients?.[0] ? `${clients?.[0]?.first_name} ${clients?.[0]?.last_name}` : '',

  [COMMON_COMPUTED_FIELDS.SECOND_CLIENT_FIRST_NAME.VARIABLE]: ({ clients }) => clients?.[1]?.first_name || '',

  [COMMON_COMPUTED_FIELDS.SECOND_CLIENT_FULL_NAME.VARIABLE]: ({ clients }) => clients?.[1] ? `${clients?.[1]?.first_name} ${clients?.[1]?.last_name}` : '',

  [COMMON_COMPUTED_FIELDS.PROPERTY_ADDRESS.VARIABLE]: ({ deal }) => dealHelpers.getTransactionAddress(deal),
  [COMMON_COMPUTED_FIELDS.STREET_ADDRESS.VARIABLE]: ({ deal }) => dealHelpers.getTransactionStreetAddress(deal),
  [COMMON_COMPUTED_FIELDS.CITY.VARIABLE]: ({ deal }) => dealHelpers.getAddressCity(deal),
  [COMMON_COMPUTED_FIELDS.STATE.VARIABLE]: ({ deal }) => dealHelpers.getAddressState(deal),
  [COMMON_COMPUTED_FIELDS.ZIP.VARIABLE]: ({ deal }) => dealHelpers.getAddressZipCode(deal),

  [COMMON_COMPUTED_FIELDS.CLOSING_DATE.VARIABLE]: (
    ({ deal }) => formatDate(dealHelpers.getTransactionClosingOn(deal))
  ),

  [COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.VARIABLE]: (
    ({ deal }) => formatDate(dealHelpers.getTransactionUnderContractDate(deal))
  ),

  [COMMON_COMPUTED_FIELDS.AGENT_FIRST_NAME.VARIABLE]: ({ agent }) => agent?.first_name,

  [COMMON_COMPUTED_FIELDS.AGENT_FULL_NAME.VARIABLE]: ({ agent }) => `${agent?.first_name} ${agent?.last_name}`,

  [COMMON_COMPUTED_FIELDS.AGENT_TITLE.VARIABLE]: ({ agent }) => userHelpers.getRealtorTitle(agent),

  [COMMON_COMPUTED_FIELDS.AGENT_LICENSE_NUMBER.VARIABLE]: ({ agent }) => agent?.type_data?.license_number,

  [COMMON_COMPUTED_FIELDS.AGENT_PHONE.VARIABLE]: ({ agent }) => agent?.phone_number,

  [COMMON_COMPUTED_FIELDS.AGENT_EMAIL.VARIABLE]: ({ agent }) => agent?.email,

  [COMMON_COMPUTED_FIELDS.BROKERAGE_NAME.VARIABLE]: ({ company }) => company?.name,

  [COMMON_COMPUTED_FIELDS.SELLER_FULL_NAMES.VARIABLE]: ({ clients, deal }) => (deal?.type === DEAL_TYPES.SELLER
    ? clients?.map((client) => `${client?.first_name} ${client?.last_name}`).join(', ')
    : deal?.advanced_fields?.[COMMON_COMPUTED_FIELDS.SELLER_FULL_NAMES.NAME] || ''),

  [COMMON_COMPUTED_FIELDS.BUYER_FULL_NAMES.VARIABLE]: ({ clients, deal }) => (deal?.type === DEAL_TYPES.BUYER
    ? clients?.map((client) => `${client?.first_name} ${client?.last_name}`).join(', ')
    : deal?.advanced_fields?.[COMMON_COMPUTED_FIELDS.BUYER_FULL_NAMES.NAME] || ''),
};

// gets advanced fields from db and transforms it to inputs
export const getFieldsInputs = (
  fields,
  renderLabel = null,
) => {
  const labelRenderer = renderLabel
    ? (field) => renderLabel(field.label, field.name)
    : (field) => field.label;

  return fields?.map(
    (field) => getInputRendererByFieldType[field?.type]?.({
      ...field,
      label: labelRenderer(field),
    }) || getInputField({
      ...field,
      label: labelRenderer(field),
    }),
  );
};

export const getComputedFieldData = ({ field, agent, clients, deal, company }) => {
  // right now we cover only case of common (not custom) fields
  const data = {
    agent,
    clients,
    deal,
    company,
  };

  // TODO: calculate computed field data by prompt, not variable
  return COMMON_COMPUTED_FIELD_FILLER_BY_VARIABLE?.[field.variable]?.(data);
};

export const getFieldsInputsByVariables = (variables, companyAdvancedFields = []) => {
  // Find the fields that match the provided variables
  const matchingFields = [];
  for (const variable of variables) {
    const field = companyAdvancedFields.find((field) => field.variable === variable);
    if (field) {
      matchingFields.push(field);
    }
  }

  // Transform the fields into inputs
  const fieldsInputs = getFieldsInputs(matchingFields);

  return fieldsInputs;
};

export const getBuyerOrSeller = (deal) => ((deal?.type === DEAL_TYPES.BUYER)
  ? COMMON_COMPUTED_FIELDS.SELLER_FULL_NAMES
  : COMMON_COMPUTED_FIELDS.BUYER_FULL_NAMES);
